import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Checkbox,
  ListItemText,
  FormGroup,
  FormControlLabel,
  Typography,
  Divider,
  Box,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Avatar,
  InputAdornment
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { task, car as carApi, user as userApi } from '../../../utils/api';
import { TASK_PRIORITY, TASK_STATUS, TASK_TYPE } from '../../../constants/task';
import { toast } from 'react-toastify';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HandymanIcon from '@mui/icons-material/Handyman';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EuroIcon from '@mui/icons-material/Euro';
import { useAuth } from '../../../components/auth/AuthModule';

const MULTIPLE_CARS_VALUE = 'multiple_cars';

// Dropdown menu component for the users with better empty state handling
const UserDropdown = ({ value, onChange, name, label, users, loading, error, required = false }) => {
  const { t } = useTranslation('workshop');
  
  return (
    <FormControl fullWidth required={required}>
      <InputLabel>{label}</InputLabel>
      <Select
        name={name}
        value={value || ''}
        onChange={onChange}
        label={label}
        displayEmpty
        disabled={loading}
        error={error}
      >
        {loading && (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography>{t('createTask.fields.loadingUsers')}</Typography>
            </Box>
          </MenuItem>
        )}
        
        {error && (
          <MenuItem disabled>
            <Typography color="error">{t('createTask.fields.errorLoadingUsers')}</Typography>
          </MenuItem>
        )}
        
        {!loading && !error && Array.isArray(users) && users.length > 0 ? (
          users.map((user) => (
            <MenuItem key={user._id} value={user._id}>
              {user.name || user.username}
            </MenuItem>
          ))
        ) : (
          !loading && !error && (
            <MenuItem disabled>
              <em>{t('createTask.fields.noUsersAvailable')}</em>
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );
};

const CreateTaskModal = ({ open, onClose, onSuccess, initialCarId, isCheckupTask }) => {
  const { t } = useTranslation('workshop');
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cars, setCars] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCars, setSelectedCars] = useState([]);
  const [carOwners, setCarOwners] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    car: initialCarId || '',
    assignedTo: '',
    priority: TASK_PRIORITY.MEDIUM,
    status: TASK_STATUS.PENDING,
    type: isCheckupTask ? TASK_TYPE.CHECKUP : TASK_TYPE.MAINTENANCE,
    estimatedHours: '',
    dueDate: format(new Date(), 'yyyy-MM-dd'),
    repairCost: '',
    paidBy: '',
    isPaid: false
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingCars, setLoadingCars] = useState(false);
  const [userFetchError, setUserFetchError] = useState(false);
  const [carFetchError, setCarFetchError] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCarsAndUsers();
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (initialCarId) {
      setFormData(prev => ({
        ...prev,
        car: initialCarId
      }));
    }
  }, [initialCarId]);

  useEffect(() => {
    // Set the current user as the default payer
    if (currentUser && currentUser._id) {
      setFormData(prev => ({
        ...prev,
        paidBy: currentUser._id
      }));
    }
  }, [currentUser]);

  useEffect(() => {
    // When a car is selected, update the form with the car owner
    if (formData.car && formData.car !== MULTIPLE_CARS_VALUE && carOwners[formData.car]) {
      console.log('Setting car owner as debtor:', carOwners[formData.car]);
    }
  }, [formData.car, carOwners]);

  // Separated fetch methods for better error handling
  const fetchCars = async () => {
    try {
      setLoadingCars(true);
      setCarFetchError(false);
      console.log('[DEBUG] Fetching cars data');
      const carsData = await carApi.getAllCars();
      
      // Ensure we have an array
      const carsArray = Array.isArray(carsData) ? carsData : [];
      console.log('[DEBUG] Processed cars array:', carsArray.length);
      
      setCars(carsArray);
      
      // Create a map of car IDs to their owners
      const ownersMap = {};
      carsArray.forEach(car => {
        if (car.user && car.user._id) {
          ownersMap[car._id] = car.user._id;
        }
      });
      setCarOwners(ownersMap);
      return true;
    } catch (error) {
      console.error('[DEBUG] Error fetching cars data:', error);
      setCarFetchError(true);
      return false;
    } finally {
      setLoadingCars(false);
    }
  };
  
  const fetchUsers = async (retryCount = 0) => {
    try {
      setLoadingUsers(true);
      setUserFetchError(false);
      console.log('[DEBUG] Fetching users data, retry:', retryCount);
      
      // Add a delay for retries to avoid overwhelming the server
      if (retryCount > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      const usersData = await userApi.getUsers();
      console.log('[DEBUG] Raw users data received:', usersData);
      
      // Ensure we have an array - try different possibilities
      let usersArray;
      if (Array.isArray(usersData)) {
        usersArray = usersData;
      } else if (usersData && Array.isArray(usersData.users)) {
        usersArray = usersData.users;
      } else if (usersData && typeof usersData === 'object') {
        // Find any array property in the response object
        const arrayProps = Object.entries(usersData)
          .filter(([_, val]) => Array.isArray(val) && val.length > 0);
        
        if (arrayProps.length > 0) {
          // Get the first array found
          usersArray = arrayProps[0][1];
        } else {
          usersArray = [];
        }
      } else {
        usersArray = [];
      }
      
      console.log('[DEBUG] Processed users array:', usersArray.length);
      setUsers(usersArray);
      return usersArray.length > 0;
    } catch (error) {
      console.error('[DEBUG] Error fetching users data:', error);
      setUserFetchError(true);
      
      // Retry up to 2 times if failed
      if (retryCount < 2) {
        console.log('[DEBUG] Retrying user fetch, attempt:', retryCount + 1);
        return fetchUsers(retryCount + 1);
      }
      return false;
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchCarsAndUsers = async () => {
    console.log('[DEBUG] Starting to fetch cars and users');
    
    // Fetch both cars and users in parallel
    const [carsSuccess, usersSuccess] = await Promise.all([
      fetchCars(),
      fetchUsers()
    ]);
    
    if (!carsSuccess) {
      toast.error(t('errors.fetchingCarsData'));
    }
    
    if (!usersSuccess) {
      toast.error(t('errors.fetchingUsersData'));
      // Display a more helpful error message for debugging
      console.error('[DEBUG] Failed to fetch users after retries. Please check server logs.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'car' && value === MULTIPLE_CARS_VALUE) {
      // When "Multiple Cars" is selected, clear the selectedCars array
      setSelectedCars([]);
    } else if (name === 'car' && value !== MULTIPLE_CARS_VALUE) {
      // When a single car is selected, clear the selectedCars array
      setSelectedCars([]);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCarCheckboxChange = (carId) => {
    setSelectedCars(prev => {
      if (prev.includes(carId)) {
        return prev.filter(id => id !== carId);
      } else {
        return [...prev, carId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Format the data correctly for the API
      const taskData = {
        ...formData,
        estimatedHours: formData.estimatedHours ? Number(formData.estimatedHours) : undefined,
        repairCost: formData.repairCost ? Number(formData.repairCost) : undefined,
        // Ensure dueDate is in ISO format
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
        // Add financial tracking data
        financialTracking: {
          repairCost: formData.repairCost ? Number(formData.repairCost) : 0,
          paidBy: formData.paidBy || (currentUser ? currentUser._id : null),
          isPaid: formData.isPaid || false,
          owedBy: formData.car !== MULTIPLE_CARS_VALUE ? carOwners[formData.car] : null
        }
      };
      
      if (formData.car === MULTIPLE_CARS_VALUE) {
        if (selectedCars.length === 0) {
          toast.error(t('errors.noCarSelected'));
          setLoading(false);
          return;
        }
        
        // Create multiple tasks, one for each selected car
        const promises = selectedCars.map(carId => {
          return task.create({
            ...taskData,
            car: carId,
            financialTracking: {
              ...taskData.financialTracking,
              owedBy: carOwners[carId] || null
            }
          });
        });
        
        await Promise.all(promises);
        toast.success(t('success.multipleTasksCreated', { count: selectedCars.length }));
      } else {
        // Create a single task
        await task.create(taskData);
        toast.success(t('success.taskCreated'));
      }
      
      onSuccess();
      resetForm();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('errors.creatingTask'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      car: initialCarId || '',
      assignedTo: '',
      priority: TASK_PRIORITY.MEDIUM,
      status: TASK_STATUS.PENDING,
      type: isCheckupTask ? TASK_TYPE.CHECKUP : TASK_TYPE.MAINTENANCE,
      estimatedHours: '',
      dueDate: format(new Date(), 'yyyy-MM-dd'),
      repairCost: '',
      paidBy: currentUser ? currentUser._id : '',
      isPaid: false
    });
    setSelectedCars([]);
  };

  const getSelectedCarsCount = () => {
    return selectedCars.length;
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case TASK_TYPE.MAINTENANCE:
        return <BuildIcon />;
      case TASK_TYPE.REPLACEMENT:
        return <SwapHorizIcon />;
      case TASK_TYPE.FIX:
        return <HandymanIcon />;
      case TASK_TYPE.OTHER:
        return <MoreHorizIcon />;
      default:
        return <BuildIcon />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('createTask.title')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                name="title"
                label={t('createTask.fields.title')}
                fullWidth
                required
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('createTask.fields.description')}
                fullWidth
                required
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            
            {/* First row of fields - Car, Assigned To, Due Date */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>{t('createTask.fields.car')}</InputLabel>
                <Select
                  name="car"
                  value={formData.car}
                  onChange={handleChange}
                  label={t('createTask.fields.car')}
                >
                  <MenuItem value={MULTIPLE_CARS_VALUE}>
                    <ListItemIcon>
                      <DirectionsCarIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('createTask.fields.multipleCars')} 
                      secondary={selectedCars.length > 0 ? t('createTask.fields.selectedCarsCount', { count: getSelectedCarsCount() }) : null}
                    />
                  </MenuItem>
                  <Divider />
                  {cars.map((car) => (
                    <MenuItem key={car._id} value={car._id}>
                      {car.name} {car.user && `(${t('createTask.fields.carOwner')}: ${car.user.name || car.user.username})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <UserDropdown
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                label={t('createTask.fields.assignedTo')}
                users={users}
                loading={loadingUsers}
                error={userFetchError}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                name="dueDate"
                label={t('createTask.fields.dueDate')}
                type="date"
                fullWidth
                required
                value={formData.dueDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            {/* Multiple Cars Selection */}
            {formData.car === MULTIPLE_CARS_VALUE && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, maxHeight: '200px', overflow: 'auto' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('createTask.fields.selectCars')}
                  </Typography>
                  <List dense>
                    {cars.map((car) => (
                      <ListItem 
                        key={car._id}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedCars.includes(car._id)}
                            onChange={() => handleCarCheckboxChange(car._id)}
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={car.name} 
                          secondary={car.user ? (car.user.name || car.user.username) : ''}
                        />
                        {car.imageUrl && (
                          <Avatar 
                            src={car.imageUrl} 
                            alt={car.name}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                  {selectedCars.length > 0 && (
                    <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('createTask.fields.selectedCarsCount', { count: getSelectedCarsCount() })}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}
            
            {/* Second row of fields - Type, Priority, Estimated Hours */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>{t('createTask.fields.type')}</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  label={t('createTask.fields.type')}
                >
                  {Object.entries(TASK_TYPE).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getTaskTypeIcon(value)}
                        </ListItemIcon>
                        <Typography>{t(`taskType.${key.toLowerCase()}`)}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>{t('createTask.fields.priority')}</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label={t('createTask.fields.priority')}
                >
                  {Object.entries(TASK_PRIORITY).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {t(`priority.${key.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                name="estimatedHours"
                label={t('createTask.fields.estimatedHours')}
                type="number"
                fullWidth
                required
                value={formData.estimatedHours}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            
            {/* Financial tracking section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                {t('createTask.financialTracking.title')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="repairCost"
                label={t('createTask.financialTracking.repairCost')}
                type="number"
                fullWidth
                value={formData.repairCost}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <UserDropdown
                name="paidBy"
                value={formData.paidBy}
                onChange={handleChange}
                label={t('createTask.financialTracking.paidBy')}
                users={users}
                loading={loadingUsers}
                error={userFetchError}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isPaid}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPaid: e.target.checked }))}
                    name="isPaid"
                  />
                }
                label={t('createTask.financialTracking.isPaid')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} variant="outlined">
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || (formData.car === MULTIPLE_CARS_VALUE && selectedCars.length === 0)}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {formData.car === MULTIPLE_CARS_VALUE 
              ? t('common.createMultiple', { count: selectedCars.length || 0 }) 
              : t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateTaskModal; 